import FJProjectStore from '@src/store/FJProjectStore';
import FJCoreConfig from '@src/core/FJCoreConfig';

interface Subtitle {
    text: string;
    start: number;
    end: number;
    isTranslated: boolean;
    transcriptionText: string;
    transcriptionWords: {
        text: string;
        start: number;
        end: number;
    }[];
}

class FJTrimVideoManager {
    private _id: string = '';
    private _videoElement: HTMLVideoElement | null = null;
    private _isPlaying: boolean = false;
    private _requestTimer: number = -1;
    private _playTime: number = 0;
    private _duration: number = 0;
    private _startTime: number = 0;
    private _endTime: number = 0;
    private _trimSubtitleList: Subtitle[] = [];

    //#region ********************* get / set ************************
    set id(id: string) {
        this._id = id;
    }
    get id(): string {
        return this._id;
    }
    set duration(duration: number) {
        this._duration = duration;
    }
    get duration(): number {
        return this._duration;
    }
    set startTime(startTime: number) {
        this._startTime = startTime;
    }
    get startTime(): number {
        return this._startTime;
    }
    set endTime(endTime: number) {
        this._endTime = endTime;
    }
    get endTime(): number {
        return this._endTime;
    }
    set trimSubtitleList(list: Subtitle[]) {
        this._trimSubtitleList = list;
    }
    get trimSubtitleList(): Subtitle[] {
        return this._trimSubtitleList;
    }
    //#endregion

    public getVideo() {
        if (!this._id) {
            return undefined;
        }
        const manager = FJProjectStore.getManager(this._id);
        return manager?.getVideoElement();
    }

    public setVideoElement(videoElement: HTMLVideoElement | null) {
        this._videoElement = videoElement;
    }

    public setSource(src: string) {
        if (!this._videoElement) {
            console.log('no video');
            return;
        }

        this._videoElement.src = src;
    }

    public seek(time: number) {
        if (!this._videoElement) {
            console.log('no video');
            return;
        }

        this._videoElement.currentTime = time;
    }

    public async play(
        progressCallback?: (time: number) => void,
        pauseCallback?: (time: number) => void,
        doneCallback?: () => void,
    ) {
        if (!this._videoElement) {
            console.log('no video');
            return;
        }

        if (this._isPlaying) {
            pauseCallback?.(0);
            return;
        }
        this._isPlaying = true;

        this._playTime = this._videoElement.currentTime;
        await this._videoElement.play();

        /** 按工程 FPS（默认 30）节拍触发回调与绘制，避免跟随显示器刷新率 */
        const frameMs = 1000 / FJCoreConfig.FPS;
        let acc = 0;
        let lastTs: number | undefined = undefined;

        const __playing = (timestamp: number) => {
            if (lastTs === undefined) lastTs = timestamp;
            acc += timestamp - lastTs;
            lastTs = timestamp;

            if (!this._isPlaying) return;
            const currentTime = this._videoElement.currentTime;
            if (currentTime >= this._duration - 1e-3 || currentTime > this._endTime - 1e-3) {
                this.pause();
                doneCallback?.();
                return;
            }

            if (acc >= frameMs) {
                acc -= frameMs;
                this._playTime = currentTime;
                if (this._playTime >= 0 && this._playTime <= this._duration) {
                    progressCallback?.(this._playTime);
                }
            }

            if (this._isPlaying) {
                this._requestTimer = window.requestAnimationFrame(__playing);
            }
        };

        if (this._requestTimer) window.cancelAnimationFrame(this._requestTimer);
        this._requestTimer = window.requestAnimationFrame(__playing);
    }
    public pause() {
        if (!this._videoElement) {
            console.log('no video');
            return;
        }

        if (this._requestTimer) {
            window.cancelAnimationFrame(this._requestTimer);
        }
        this._videoElement.pause();
        this._isPlaying = false;
    }

    public clear() {
        this._id = '';
        this._trimSubtitleList = [];
        this.pause();
    }
}

const manager = new FJTrimVideoManager();
export default manager;
